import React, { useState, useEffect } from 'react';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';
import { useConsultations } from '../hooks/useConsultations';
import LockIcon from '@/assets/image/lock.svg?react';
import { DietPlanWidget } from '../components/diet/DietPlanWidget';
import { WeightCard } from '../components/diet/WeightCard';
import { StartTherapyCard } from '../components/diet/StartTherapyCard';
import { AddMeasurementModal } from '../components/diet/AddMeasurementModal';
import { getMyDietPlan, getWeightHistory } from '../api/dietApi';
import type { DietPlan, WeightMeasurement } from '../api/dietApi';

interface TherapyPageProps {
  onNavigateToVisits?: () => void;
}

export const TherapyPage: React.FC<TherapyPageProps> = ({ onNavigateToVisits }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { consultations, upcoming, isLoading: consLoading } = useConsultations();

  const [dietPlan, setDietPlan] = useState<DietPlan | null>(null);
  const [weightMeasurements, setWeightMeasurements] = useState<WeightMeasurement[]>([]);
  const [isWeightLoading, setIsWeightLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    // ── Instant load з prefetch-кешу ──────────────────────────────────────────
    // AuthContext запускає prefetch одразу після /users/me.
    // Якщо дані вже є (найчастіший сценарій) — відображаємо миттєво.
    const prefetch = window.__prefetchDiet;
    const PREFETCH_MAX_AGE_MS = 60_000; // 60 секунд — після цього prefetch вважається застарілим

    // ВАЖЛИВО: використовуємо prefetch ТІЛЬКИ якщо weightHistory успішно завантажено.
    // Якщо prefetch впав (weightHistory === null, наприклад мережева помилка на мобільному)
    // — видаляємо його і робимо звичайний запит. Інакше компонент покаже порожній
    // графік і зробить early return без retry.
    const isPrefetchValid =
      prefetch !== undefined &&
      Date.now() - prefetch.loadedAt < PREFETCH_MAX_AGE_MS &&
      prefetch.weightHistory !== null; // null = запит впав, не використовуємо

    if (isPrefetchValid && prefetch) {
      setDietPlan(prefetch.dietPlan);
      setWeightMeasurements(prefetch.weightHistory!.measurements ?? []);
      setIsWeightLoading(false);
      // Очищаємо щоб при повторному відкритті сторінки отримати свіжі дані
      delete window.__prefetchDiet;
      return;
    }

    // Якщо prefetch є але невалідний (впав або застарів) — прибираємо сміття
    if (prefetch) delete window.__prefetchDiet;

    // Fallback: prefetch не готовий, впав або застарів — робимо звичайний запит
    Promise.all([
      getMyDietPlan().catch(() => null),
      getWeightHistory().catch(() => ({ measurements: [], start_weight: null, current_weight: null })),
    ]).then(([plan, weightData]) => {
      setDietPlan(plan);
      setWeightMeasurements(weightData?.measurements ?? []);
    }).finally(() => {
      setIsWeightLoading(false);
    });
  }, []);

  // Safety check
  if (user?.personaType !== 'A' && user?.isWeightLoss !== true) {
    return null;
  }

  // ── Логіка «холодного старту» (Сценарій 5 прототипу) ──────────────────────
  // noTherapy = !S.control && !S.cons.length (умова з прототипу)
  // Пацієнтка купила послуги дієтолога, але немає терапії/консультацій лікаря
  const isColdStart = !consLoading
    && !user?.nextControlVisitDate
    && consultations.length === 0
    && dietPlan !== null
    && dietPlan.status !== 'none';

  // ── Підзаголовок ──────────────────────────────────────────────────────────
  const subtitle = isColdStart
    ? 'Poniżej znajdziesz swoje usługi i wszystko, co przyda Ci się na starcie'
    : t('ther_welcome_sub');

  // ── Стан меню консультацій ─────────────────────────────────────────────────
  let visitsState = 1;
  if (!consLoading) {
    if (upcoming.length > 0) visitsState = 1;
    else if (consultations.length > 0) visitsState = 2;
    else visitsState = 3;
  } else {
    visitsState = 2;
  }

  // ── Після збереження нового виміру ──────────────────────────────────────
  const handleWeightSaved = (measurement: WeightMeasurement) => {
    // Вставляємо в масив з сортуванням за датою
    setWeightMeasurements(prev => {
      const updated = [...prev.filter(m => m.iso_date !== measurement.iso_date), measurement];
      updated.sort((a, b) => a.iso_date.localeCompare(b.iso_date));
      return updated;
    });
  };

  // ── Рядок-картка «Moje konsultacje» ──────────────────────────────────────
  const konsultacjeRow = (
    <div
      className={`cab-card cab-menu-card ${visitsState === 3 ? 'cab-menu-card-dashed' : ''}`}
      data-tour="visits-tile"
      onClick={onNavigateToVisits}
      style={{ cursor: 'pointer', marginBottom: 12 }}
    >
      <div className="cab-menu-ic">
        {visitsState === 3 ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 21V19C19 17.9391 18.5786 16.9217 17.8284 16.1716C17.0783 15.4214 16.0609 15 15 15H9C7.93913 15 6.92172 15.4214 6.17157 16.1716C5.42143 16.9217 5 17.9391 5 19V21" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div className="cab-menu-text">
        <div className="cab-menu-title">
          {t('ther_my_visits')}
          {visitsState === 1 && <span className="cab-badge">{upcoming.length}</span>}
        </div>
        <div className="cab-menu-desc">{t('ther_visits_desc')}</div>
      </div>
      <div className="cab-menu-arrow">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    </div>
  );

  // ── Рядок «Badania kontrolne» ─────────────────────────────────────────────
  const badaniaRow = (
    <div
      className="cab-card cab-menu-card"
      onClick={() => {
        if (typeof window !== 'undefined') {
          const siteUrl = (window as any).cabinetVars?.siteUrl || '';
          window.location.href = siteUrl + '/usluga/metaboliczna-rownowaga-otylosc/';
        }
      }}
      style={{ cursor: 'pointer', marginBottom: 12 }}
    >
      <div className="cab-menu-ic">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M22 11.0779V11.9979C21.9988 14.1543 21.3005 16.2526 20.0093 17.9797C18.7182 19.7069 16.9033 20.9704 14.8354 21.5818C12.7674 22.1932 10.5573 22.1198 8.53447 21.3725C6.51168 20.6252 4.78465 19.244 3.61096 17.435C2.43727 15.6259 1.87979 13.486 2.02168 11.3342C2.16356 9.18248 2.99721 7.13424 4.39828 5.49498C5.79935 3.85573 7.69279 2.71329 9.79619 2.23806C11.8996 1.76282 14.1003 1.98025 16.07 2.85791" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M22 4L12 14.01L9 11.01" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="cab-menu-text">
        <div className="cab-menu-title">{t('ther_tests')}</div>
        <div className="cab-menu-desc">{t('ther_tests_desc')}</div>
      </div>
      <div className="cab-menu-arrow">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    </div>
  );

  // ── Рядок «Usługi dietetyka» ──────────────────────────────────────────────
  const dietetykRow = (
    <div
      className="cab-card cab-menu-card"
      onClick={() => {
        if (typeof window !== 'undefined') {
          const siteUrl = (window as any).cabinetVars?.siteUrl || '';
          window.location.href = siteUrl ? `${siteUrl}/dietetyk-online/` : '/dietetyk-online/';
        }
      }}
      style={{ cursor: 'pointer', marginBottom: 12 }}
    >
      <div className="cab-menu-ic">
        {/* heart icon */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 20l-7-7a4 4 0 0 1 6-5l1 1 1-1a4 4 0 0 1 6 5z" />
        </svg>
      </div>
      <div className="cab-menu-text">
        <div className="cab-menu-title">
          Usługi dietetyka
          <span className="cab-badge-new">Nowość</span>
        </div>
        <div className="cab-menu-desc">Jadłospis, konsultacja online lub pakiet</div>
      </div>
      <div className="cab-menu-arrow">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    </div>
  );

  // ── Portal wiedzy (тільки для звичайного стану, без cold start) ───────────
  const portalRow = !isColdStart ? (
    <div
      className="cab-card cab-menu-card"
      onClick={() => {
        if (typeof window !== 'undefined') {
          window.location.href = 'https://portal.tabletka24.pl/';
        }
      }}
      style={{ cursor: 'pointer', marginBottom: 12 }}
    >
      <div className="cab-menu-ic">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2.5 3H19.5C19.5 3 21.5 4 21.5 6.5C21.5 9 19.5 10 19.5 10H2.5C2.5 10 4.5 9 4.5 6.5C4.5 4 2.5 3 2.5 3ZM21.5 14H4.5C4.5 14 2.5 15 2.5 17.5C2.5 20 4.5 21 4.5 21H21.5C21.5 21 19.5 20 19.5 17.5C19.5 15 21.5 14 21.5 14Z" stroke="#57437E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="cab-menu-text">
        <div className="cab-menu-title">{t('ther_portal')}</div>
        <div className="cab-menu-desc">{t('ther_portal_desc')}</div>
      </div>
      <div className="cab-menu-arrow">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    </div>
  ) : null;

  return (
    <div className="cab-page-wrap">
      <h1 className="cab-page-title">{t('greet_title', { name: user?.firstName || '' })}</h1>
      <p className="cab-welcome-sub">{subtitle}</p>

      {/* ── Сценарій 5: Cold start — «Тільки раціон, без терапії» ── */}
      {isColdStart ? (
        <>
          {/* 1. DietPlanWidget першим — те що куплено */}
          <DietPlanWidget plan={dietPlan} />

          {/* 2. Картка «Zacznij terapię» */}
          <StartTherapyCard />

          {/* 3. WeightCard порожній */}
          <WeightCard
            measurements={weightMeasurements}
            nextControlVisitDate={null}
            onAddMeasurement={() => setIsModalOpen(true)}
          />

          {/* 4. Moje konsultacje (порожній список) */}
          {konsultacjeRow}
        </>
      ) : (
        /* ── Звичайний стан (Сценарії 1–4) ── */
        <>
          {/* 1. WeightCard (Twoja waga + Wizyta kontrolna якщо є дата) */}
          {!isWeightLoading && (
            <WeightCard
              measurements={weightMeasurements}
              nextControlVisitDate={user?.nextControlVisitDate || null}
              onAddMeasurement={() => setIsModalOpen(true)}
              onBookVisit={() => {
                window.location.href = '/usluga/kontrolna-telekonsultacja-ab/#telekonsultacja_form';
              }}
            />
          )}

          {/* 2. Moje konsultacje */}
          {konsultacjeRow}

          {/* 3. DietPlanWidget — після консультацій */}
          <DietPlanWidget plan={dietPlan} />

          {/* 4. Badania kontrolne */}
          {badaniaRow}

          {/* 5. Usługi dietetyka */}
          {dietetykRow}

          {/* 6. Portal wiedzy */}
          {portalRow}
        </>
      )}

      {/* Попап «+ Dodaj pomiar» */}
      <AddMeasurementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSaved={handleWeightSaved}
      />

      <style>{`
        .cab-page-wrap {
          text-align: left;
          padding-top: 10px;
        }
        .cab-page-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 24px;
          color: var(--cab-violet);
          margin-bottom: 4px;
        }
        .react-cabinet-isolated .cab-welcome-sub {
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 14px;
          color: var(--cab-body, #57437E);
          margin-bottom: 16px;
          line-height: 1.5;
        }
        .cab-menu-card {
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          border-radius: 24px;
          padding: 16px 20px;
          background: #FAF8FE;
          border: none;
          transition: background 0.2s, border-color 0.2s;
          box-shadow: none !important;
          margin-bottom: 0;
        }
        .cab-menu-card:hover {
          background: #F4F0FA;
        }
        .cab-menu-card-dashed {
          border: 1px dashed #D6C1F1;
        }
        .cab-menu-card-dashed:hover {
          background: #FAF8FE;
        }
        .cab-menu-ic {
          width: 48px;
          height: 48px;
          background: #fff;
          border-radius: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #361777;
          flex-shrink: 0;
          box-shadow: none;
        }
        .cab-menu-card-dashed .cab-menu-ic {
          box-shadow: none;
          border: 1px solid #E6D6FA;
        }
        .cab-menu-text {
          flex: 1;
        }
        .cab-menu-title {
          font-size: 16px;
          font-weight: 600;
          color: #361777;
          margin-bottom: 2px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cab-badge {
          background: #D6C1F1;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 100px;
        }
        .cab-badge-new {
          display: inline-flex;
          align-items: center;
          padding: 3px 10px;
          border-radius: 100px;
          background: #D6C1F1;
          color: #fff;
          font-size: 11px;
          font-weight: 500;
          line-height: 140%;
          white-space: nowrap;
        }
        .cab-menu-desc {
          font-size: 13px;
          color: #786798;
          line-height: 1.4;
        }
        .cab-menu-arrow {
          color: #786798;
          display: flex;
          align-items: center;
        }

        @media (min-width: 1024px) {
          .cab-page-wrap {
            padding-top: 0px;
          }
          .cab-page-title {
            font-size: 32px;
          }
          .react-cabinet-isolated .cab-welcome-sub {
            font-size: 16px;
            margin-bottom: 24px;
          }
          .cab-menu-ic, .cab-calendar-badge {
            width: 80px;
            height: 80px;
            background: #FAF8FE !important;
            border-radius: 20px;
          }
          .cab-menu-card {
            background: #FFFFFF;
            box-shadow: 0 4px 24px rgba(54, 23, 119, 0.05) !important;
          }
          .cab-menu-card-dashed {
            box-shadow: none !important;
          }
          .cab-menu-card-dashed:hover {
            background: #FFFFFF;
          }
        }
      `}</style>
    </div>
  );
};
